import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLine, MaskLineInView } from "@/components/motion/Reveal";
import { BRAND, IMAGES, MANIFESTO, HERO_SLIDES } from "@/data/site";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#FAF9F6]">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
          <AnimatePresence>
            <motion.div
              key={slide}
              data-testid={`hero-slide-${slide}`}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 2.2, ease: "easeInOut" } }}
            >
              <motion.img
                src={HERO_SLIDES[slide]}
                alt="Premium neo-classical interior"
                className="w-full h-full object-cover"
                initial={{ scale: 1 }}
                animate={{ scale: 1.08 }}
                transition={{ duration: 8, ease: "linear" }}
              />
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0 bg-[#1A1A1A]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-[#1A1A1A]/40" />
        </motion.div>

        <motion.div style={{ y: overlayY }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="overline text-[#C5A880] mb-6"
          >
            Bespoke Interior Studio &mdash; Noida &amp; Delhi NCR
          </motion.div>

          <h1 className="font-serif-display text-[#FAF9F6] font-medium leading-[0.95] text-[clamp(2.75rem,9vw,8rem)] tracking-[-0.02em]">
            <MaskLine delay={0.15}>The Art of Space.</MaskLine>
            <MaskLine delay={0.32} className="italic text-[#C5A880]">Engineered.</MaskLine>
          </h1>

          <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="text-[#FAF9F6]/80 text-base md:text-lg max-w-md leading-relaxed"
            >
              {BRAND.tagline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
            >
              <Link
                to="/portfolio"
                data-testid="hero-explore-cta"
                className="group inline-flex items-center gap-4 bg-[#FAF9F6] text-[#1A1A1A] px-8 py-4 hover:bg-[#C5A880] transition-colors duration-400"
              >
                <span className="text-sm tracking-wide">Explore the Collection</span>
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#FAF9F6]/50"
        >
          <ArrowDown size={20} className="animate-bounce" />
        </motion.div>

        <div data-testid="hero-slide-indicators" className="absolute bottom-9 right-6 md:right-10 z-10 flex items-center gap-2.5">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="h-[3px] rounded-full transition-all duration-500"
              style={{
                width: i === slide ? 34 : 14,
                backgroundColor: i === slide ? "#C5A880" : "rgba(250,249,246,0.4)",
              }}
            />
          ))}
        </div>
      </section>

      {/* EDITORIAL PHILOSOPHY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid md:grid-cols-12 gap-y-16 gap-x-10 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overline text-[#8B7355] mb-8">01 &mdash; The Philosophy</div>
            </Reveal>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[#1A1A1A]">
              <MaskLineInView>Symmetry meets</MaskLineInView>
              <MaskLineInView delay={0.1}>structural</MaskLineInView>
              <MaskLineInView delay={0.2} className="italic text-[#C5A880]">perfection.</MaskLineInView>
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-md">
                As the best interior design studio in Noida, we craft spaces utilising premium materials &mdash; like Century Club Prime plywood &mdash; and flawless spatial architecture. Every proportion is measured, every reveal intentional.
              </p>
              <Link
                to="/services"
                data-testid="philosophy-services-link"
                className="inline-flex items-center gap-3 mt-10 text-[#1A1A1A] border-b border-[#1A1A1A]/30 pb-1 hover:text-[#C5A880] hover:border-[#C5A880] transition-colors duration-300 group"
              >
                <span>Our Services</span>
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="grid grid-cols-5 gap-5">
              <Reveal className="col-span-3 pt-16" y={60}>
                <div className="overflow-hidden">
                  <img src={IMAGES.philosophyA} alt="Elegant living interior" className="w-full h-[340px] md:h-[440px] object-cover hover:scale-105 transition-transform duration-[900ms]" />
                </div>
              </Reveal>
              <Reveal className="col-span-2" delay={0.15} y={60}>
                <div className="overflow-hidden">
                  <img src={IMAGES.philosophyB} alt="Luxury kitchen detail" className="w-full h-[280px] md:h-[360px] object-cover hover:scale-105 transition-transform duration-[900ms]" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#1A1A1A] text-[#FAF9F6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <Reveal>
                <div className="overline text-[#C5A880] mb-6">02 &mdash; Manifesto</div>
                <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
                  The principles behind every project.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <div className="divide-y divide-[#FAF9F6]/10 border-t border-[#FAF9F6]/10">
                {MANIFESTO.map((m, i) => (
                  <Reveal key={m.n} delay={i * 0.08}>
                    <div className="py-8 md:py-10 grid grid-cols-12 gap-4 items-baseline group">
                      <div className="col-span-2 font-serif-display text-2xl md:text-3xl text-[#C5A880] group-hover:translate-x-1 transition-transform duration-300">{m.n}</div>
                      <h3 className="col-span-10 md:col-span-4 font-serif-display text-xl md:text-2xl">{m.title}</h3>
                      <p className="col-span-12 md:col-span-6 text-[#FAF9F6]/65 leading-relaxed text-sm md:text-base">{m.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPOTLIGHT GRID */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <Reveal>
          <div className="overline text-[#8B7355] mb-6">03 &mdash; Signature Details</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] max-w-2xl text-[#1A1A1A]">
            Details that define a <span className="italic text-[#C5A880]">Haven</span>.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-16">
          {[
            { img: IMAGES.fluted, title: "Bespoke Arched Fluted Panels", desc: "Hand-detailed arched fluting in warm veneer, catching light across the day." },
            { img: IMAGES.wardrobe, title: "Customized Wardrobe Curation", desc: "Engineered storage architecture, tailored to the millimetre for effortless living." },
          ].map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12} y={60}>
              <div data-testid={`spotlight-card-${i}`} className="group relative overflow-hidden bg-[#1A1A1A]">
                <img src={s.img} alt={s.title} className="w-full h-[420px] md:h-[560px] object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-[900ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#FAF9F6]">{s.title}</h3>
                  <p className="mt-3 text-[#FAF9F6]/75 max-w-sm leading-relaxed text-sm md:text-base">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <section className="border-y border-[#E5E5E5] py-8 md:py-10 bg-[#F2EFEB]">
        <Marquee speed={38} gradient={false} autoFill>
          {["Interior Design Noida", "Turnkey Execution", "Neo-Classical Luxury", "Interior Decor", "Bespoke Joinery", "Spatial Curation", "Premium Materials", "Investment-Driven Design"].map((t) => (
            <span key={t} className="font-serif-display text-3xl md:text-5xl text-[#1A1A1A]/80 mx-10 flex items-center gap-10">
              {t} <span className="text-[#C5A880]">&#10022;</span>
            </span>
          ))}
        </Marquee>
      </section>
    </div>
  );
};

export default Home;
